import { localizedStrings } from '@core/strings'
import React, { useMemo } from 'react'
import { PermitDetailsHeader } from './permitDetailsHeader/PermitDetailsHeader'
import { Box, Flex, Text } from '@fundamentals'
import { NavigationAwareTabs } from '@elements'
import { PermitTab, PermitTimelineTab } from '@modules/permitDetails/tabs'
import { PermitDetailsProps } from './types'
import { Button } from '@common/material/Button'
import { PageUrls } from '@core/page-urls'
import LaunchIcon from '@mui/icons-material/Launch'
import { IconButton } from '@common/material'
import ClearIcon from '@mui/icons-material/Clear'
import { useModal } from '@common/GlobalModal'
import BackdropLoader from '@common/fundamentals/BackdropLoader'
import {
  useGetPermitLinkedProcesses,
  usePermitById,
  usePermitTasks,
} from '@core/react-query/features/permits/permit'
import { PermitDetailsSidebar } from '@modules/permitDetails/PermitDetailsSidebar'
import { useRealTimePermit } from '@core/react-query/features/permits/permit/hooks/useRealTimePermit'
import { useToast } from '@core/toast'
import { LinkedDocumentsList } from '@elements/LinkingProcesses/LinkedDocumentsList'
import { ParentChildPermitsList } from '@modules/permitDetails/ParentChildPermitsList'
import LinkIcon from '@mui/icons-material/Link'
import { LinkedRegisterItemsGroupedByRegister } from '@common/LinkedRegisterItemsGroupedByRegister'
import { PermitTasksTab } from '@modules/permitDetails/tabs/PermitTasksTab'
import { Palette } from '@core/theme/color-palette-siteassist'
import { useProductAccess } from 'webapp/providers/authentication'
import useConstants from '@core/providers/useConstants'

export const PermitDetails: React.FC<PermitDetailsProps> = ({
  organisationId,
  projectId,
  permitId,
  isModal,
}) => {
  const { hideModal } = useModal()
  const toast = useToast()
  const { isViewMode } = useProductAccess()
  const { constants } = useConstants()
  const navBarHeight = constants.nav_bar_height
  const bgColor = isModal ? Palette.Common.White : Palette.Grey[50]

  const {
    data: permit,
    isLoading,
    isFetching,
    error,
  } = usePermitById({ permitId })
  useRealTimePermit(permitId, {
    enabled: !isFetching,
  })

  const { data } = useGetPermitLinkedProcesses(
    {
      permitId,
    },
    {
      onError: (e) => toast.error(e.message),
    },
  )

  const { data: tasks } = usePermitTasks(
    { permitId: permit?.id },
    {
      onError: (error) => toast.error(error?.message),
    },
  )

  const linkedPermits = data?.getLinkedPermits() || []
  const linkedChecklists = data?.getLinkedChecklists() || []
  const linkedInspections = data?.getLinkedInspections() || []
  const linkedRegisterItemsGroupedByRegister =
    data?.getLinkedProcessItemsByRegister() || []

  const tabs = useMemo(() => {
    const tabBuilder = NavigationAwareTabs.TabBuilder().addTab({
      name: 'Details',
      label: localizedStrings.details,
      component: <PermitTab permit={permit} />,
    })

    tabBuilder.addTab({
      name: 'Timeline',
      label: localizedStrings.timeline,
      clearParams: true,
      component: <PermitTimelineTab permit={permit} />,
    })

    if (tasks?.length) {
      tabBuilder.addTab({
        name: 'Tasks',
        label: 'Tasks',
        icon: <LinkIcon />,
        badgeCount: tasks.length,
        component: (
          <PermitTasksTab
            tasks={tasks}
            organisationId={organisationId}
            projectId={projectId}
            permitStartsOn={permit?.startsOn}
          />
        ),
      })
    }

    if (linkedPermits.length) {
      tabBuilder.addTab({
        name: 'Permits',
        icon: <LinkIcon />,
        label: localizedStrings.permits,
        badgeCount: linkedPermits.length,
        component: (
          <LinkedDocumentsList
            items={linkedPermits}
            organisationId={organisationId}
            permitId={permitId}
          />
        ),
      })
    }

    const linkedPermitCount = permit?.getLinkedPermitCount() ?? 0
    if (linkedPermitCount > 0) {
      tabBuilder.addTab({
        name: 'LinkedPermits',
        icon: <LinkIcon />,
        label: permit?.isChildPermit()
          ? localizedStrings.linkedPermit
          : localizedStrings.linkedPermits,
        badgeCount: linkedPermitCount,
        component: (
          <ParentChildPermitsList
            parentPermit={permit?.parentPermit}
            childPermits={permit?.children}
            organisationId={organisationId}
          />
        ),
      })
    }

    if (linkedChecklists.length) {
      tabBuilder.addTab({
        name: 'Checklists',
        icon: <LinkIcon />,
        label: localizedStrings.checklists,
        badgeCount: linkedChecklists.length,
        component: (
          <LinkedDocumentsList
            items={linkedChecklists}
            organisationId={organisationId}
            permitId={permitId}
          />
        ),
      })
    }

    if (linkedInspections.length) {
      tabBuilder.addTab({
        name: 'Inspections',
        icon: <LinkIcon />,
        label: localizedStrings.inspections,
        badgeCount: linkedInspections.length,
        component: (
          <LinkedDocumentsList
            items={linkedInspections}
            organisationId={organisationId}
            permitId={permitId}
          />
        ),
      })
    }

    if (linkedRegisterItemsGroupedByRegister.length) {
      tabBuilder.addTab({
        name: 'RegisterItems',
        icon: <LinkIcon />,
        label: localizedStrings.registerItems,
        badgeCount: data?.getLinkedRegisterItems()?.length,
        component: (
          <LinkedRegisterItemsGroupedByRegister
            linkedRegisterItemsGroupedByRegister={
              linkedRegisterItemsGroupedByRegister
            }
            organisationId={organisationId}
          />
        ),
      })
    }

    return tabBuilder.build()
  }, [permit, data, tasks])

  if (error?.axiosError?.response?.status === 403) {
    return (
      <Flex flex={1} justifyContent='center' alignItems='center'>
        <Box mb={10}>
          <Text variant='h5'>{localizedStrings.invalidPermissions}</Text>
          <Text color='grey'>{localizedStrings.permitNoPermission}</Text>
        </Box>
      </Flex>
    )
  }

  const stickyPosition = {
    HEADER: isModal ? 0 : `calc(${navBarHeight}px + 16px)`,
    TAB_HEADER: isModal ? '143px' : '167px',
  }

  return (
    <>
      <BackdropLoader open={!permit && isLoading} />
      {!!permit && (
        <>
          <Box
            data-test='permit-details'
            position='sticky'
            top={stickyPosition.HEADER}
            zIndex={10}
            bgcolor={bgColor}
            px='3'
          >
            {isModal && (
              <Flex justifyContent={!isViewMode ? 'space-between' : 'flex-end'}>
                {!isViewMode && (
                  <Button
                    style={{ marginBottom: 4 }}
                    startIcon={<LaunchIcon />}
                    onClick={() => {
                      window.open(
                        PageUrls.projectPermitDetail({
                          organisationId,
                          projectId: permit?.project?.id,
                          permitId,
                        }),
                        '_blank',
                        'noopener, noreferrer',
                      )
                    }}
                  >
                    {localizedStrings.openInNewTab}
                  </Button>
                )}
                <IconButton aria-label='close-modal' onClick={hideModal}>
                  <ClearIcon />
                </IconButton>
              </Flex>
            )}
            <PermitDetailsHeader permit={permit} />
          </Box>
          <Flex>
            <NavigationAwareTabs
              tabs={tabs}
              withoutQueryParam={isModal}
              tabHeaderSx={{
                position: 'sticky',
                top: stickyPosition.TAB_HEADER,
                zIndex: 20,
                bgcolor: bgColor,
              }}
            />

            <Box
              pl={3}
              pb={3}
              width='23rem'
              position='sticky'
              top={stickyPosition.TAB_HEADER}
              maxHeight={`calc(100vh - ${stickyPosition.TAB_HEADER})`}
              overflow='auto'
            >
              <PermitDetailsSidebar
                organisationId={organisationId}
                projectId={projectId}
                permit={permit}
              />
            </Box>
          </Flex>
        </>
      )}
    </>
  )
}
